import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Input, Menu, Popover, Table } from "antd";
import { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DeleteProductCategory } from "../../redux/actions/productCategory/deleteProductCategoryAction";
import { loadSingleProductCategory } from "../../redux/actions/productCategory/detailProductCategoryAction";
import Loader from "../loader/loader";
import GenerateBarcodePopUp from "../product/generateBarcodePopUp";

const PRODUCT_COLUMNS = [
  {
    title: "Image",
    dataIndex: "imageUrl",
    key: "imageUrl",
    render: (imageUrl) => (
      <img style={{ maxWidth: "40px" }} alt="product" src={imageUrl} />
    ),
  },
  { title: "SKU", dataIndex: "sku", key: "sku" },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (name, { id }) => <Link to={`/product/${id}`}>{name}</Link>,
  },
  { title: "Quantity", dataIndex: "quantity", key: "quantity" },
  { title: "Purchase price", dataIndex: "purchase_price", key: "purchase_price", responsive: ["md"] },
  { title: "Sale price", dataIndex: "sale_price", key: "sale_price", responsive: ["md"] },
  { title: "Unit Type", dataIndex: "unit_type", key: "unit_type" },
  { title: "Reorder QTY", dataIndex: "reorder_quantity", key: "reorder_quantity" },
  {
    title: "Action",
    key: "action",
    render: (_, record) => <GenerateBarcodePopUp sku={record.sku ? record.sku : 0} />,
  },
];

const TABLE_SCROLL_Y = 360;

function ProductTable({ list, columnsToShow }) {
  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));
  return (
    <div className="pcat-product-list-card">
      <div className="detail-page-table-wrap">
        <Table
          loading={!list}
          columns={columnsToShow}
          dataSource={list ? addKeys(list) : []}
          size="small"
          scroll={{ y: TABLE_SCROLL_Y }}
        />
      </div>
    </div>
  );
}

const DetailProductCategory = () => {
  const { id } = useParams();
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const category = useSelector((state) => state.productCategories.category);

  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState(PRODUCT_COLUMNS);
  const [productSearch, setProductSearch] = useState("");

  useEffect(() => {
    const items = PRODUCT_COLUMNS.map((item) => ({
      key: item.key,
      label: <span>{item.title}</span>,
    }));
    setColumnItems(items);
    setColumnsToShow(PRODUCT_COLUMNS);
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      setColumnsToShow((prev) => prev.filter((item) => item.key !== col.key));
    } else {
      const foundIndex = PRODUCT_COLUMNS.findIndex((item) => item.key === col.key);
      const foundCol = PRODUCT_COLUMNS.find((item) => item.key === col.key);
      setColumnsToShow((prev) => {
        const next = [...prev];
        next.splice(foundIndex, 0, foundCol);
        return next;
      });
    }
  };

  const onDelete = () => {
    try {
      dispatch(DeleteProductCategory(id));

      setVisible(false);
      toast.warning(`Product : ${category.name} is removed `);
      return navigate("/product-category");
    } catch (error) {
      console.log(error.message);
    }
  };
  // Delete Supplier PopUp
  const [visible, setVisible] = useState(false);

  const handleVisibleChange = (newVisible) => {
    setVisible(newVisible);
  };

  useEffect(() => {
    dispatch(loadSingleProductCategory(id));
  }, [id, dispatch]);

  // Refetch when user returns to this page (e.g. after editing a product's category) so list stays in sync
  useEffect(() => {
    const onFocus = () => dispatch(loadSingleProductCategory(id));
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [id, dispatch]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));

  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }

  return (
    <div className="pcat-detail-page">
      <div className="pcat-detail-wrap">
        {category ? (
          <Fragment key={category.id}>
            <Card bordered={false} className="pcat-detail-card">
              <div className="pcat-detail-header">
                <h5 className="pcat-detail-title">
                  <i className="bi bi-folder2" aria-hidden />
                  <span className="pcat-detail-name">{category.name}</span>
                </h5>
                <div className="pcat-detail-actions">
                  <Link
                    to={`/product-category/${category.id}/update`}
                    state={{ data: category }}>
                    <Button type="primary" shape="round" icon={<EditOutlined />} aria-label="Edit category" />
                  </Link>
                  <Popover
                    content={
                      <a onClick={onDelete}>
                        <Button type="primary" danger>Yes Please !</Button>
                      </a>
                    }
                    title="Are you sure you want to delete ?"
                    trigger="click"
                    visible={visible}
                    onVisibleChange={handleVisibleChange}>
                    <Button type="danger" shape="round" icon={<DeleteOutlined />} aria-label="Delete category" />
                  </Popover>
                  {category?.product && (
                    <>
                      <Dropdown
                        overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                        placement="bottomRight"
                      >
                        <Button size="small" className="column-visibility">Column Visibility</Button>
                      </Dropdown>
                      <CSVLink
                        data={category.product}
                        className="btn btn-dark btn-sm"
                        filename={`category_${category.name}`}>
                        Download CSV
                      </CSVLink>
                    </>
                  )}
                </div>
              </div>

              <div className="pcat-products-section">
                <div className="pcat-products-section-header">
                  <h5 className="pcat-products-heading">Products under {category.name}</h5>
                  <Input
                    placeholder="Search products (name, SKU)"
                    allowClear
                    size="small"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pcat-products-search"
                  />
                </div>
                <ProductTable
                  list={
                    category?.product?.filter(
                      (p) =>
                        !productSearch ||
                        (p.name && p.name.toLowerCase().includes(productSearch.toLowerCase())) ||
                        (p.sku && String(p.sku).toLowerCase().includes(productSearch.toLowerCase()))
                    )
                  }
                  columnsToShow={columnsToShow}
                />
              </div>
            </Card>
          </Fragment>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};

export default DetailProductCategory;
